<script setup lang="ts">
  import { computed, onMounted, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import AppHeader from '../components/AppHeader.vue'
  import CustomTag from '../components/CustomTag.vue'
  import DetailsLayout from '../components/DetailsLayout.vue'
  import { useToast } from '../composables/useToast'
  import { useFacturenStore } from '../stores/facturen'
  import { sanitizeFileName, triggerFileDownload } from '../utils/fileUtils'
  import type { FactuurHerinnering, FactuurProduct, FactuurStatus } from '../../../../types'

  import Button from 'primevue/button'
  import Message from 'primevue/message'

  const route = useRoute()
  const toast = useToast()
  const facturenStore = useFacturenStore()

  const factuurId = computed(() => String(route.params.id || ''))
  const factuur = computed(() => facturenStore.currentFactuur)

  const statusLabels: Record<FactuurStatus, string> = {
    concept: 'Concept',
    verzonden: 'Verzonden',
    betaald: 'Betaald',
    vervallen: 'Vervallen',
    gecrediteerd: 'Gecrediteerd',
  }

  const statusColors: Record<FactuurStatus, string> = {
    concept: 'nieuw',
    verzonden: 'aangepast',
    betaald: 'alles_geaccepteerd',
    vervallen: 'afgewezen',
    gecrediteerd: 'afgewezen',
  }

  const factuurTitle = computed(() => factuur.value?.factuurnr || 'Conceptfactuur')
  const opdrachtgeverLabel = computed(
    () => factuur.value?.opdrachtgever?.display_name || factuur.value?.opdrachtgever?.email || 'Onbekende opdrachtgever',
  )
  const inspectiebureauLabel = computed(
    () => factuur.value?.ibedrijf?.users?.display_name || factuur.value?.ibedrijf?.contactnaam || 'Onbekend inspectiebureau',
  )
  const isOverdue = computed(() => {
    if (!factuur.value?.vervaldatum) return false
    if (factuur.value.status === 'betaald' || factuur.value.status === 'vervallen') return false

    const dueDate = new Date(factuur.value.vervaldatum)
    if (Number.isNaN(dueDate.getTime())) return false

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    dueDate.setHours(0, 0, 0, 0)
    return dueDate < today
  })

  function getStatusLabel(status: FactuurStatus) {
    return statusLabels[status] || status
  }

  function getStatusColor(status: FactuurStatus) {
    return statusColors[status] || 'info'
  }

  function formatDate(dateString: string | null, includeTime = false) {
    if (!dateString) return '-'

    try {
      return new Intl.DateTimeFormat('nl-NL', includeTime
        ? {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }
        : {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }).format(new Date(dateString))
    } catch {
      return '-'
    }
  }

  function formatCurrency(amount: number | null | undefined) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0)
  }

  async function loadFactuur() {
    if (!factuurId.value) return
    await facturenStore.fetchFactuurById(factuurId.value)
  }

  async function downloadPdf() {
    if (!factuurId.value) return

    try {
      const factuurnr = factuur.value?.factuurnr
      const fileName = factuurnr ? `${sanitizeFileName(factuurnr)}.pdf` : undefined
      const signedUrl = await facturenStore.getFactuurPdfSignedUrl(factuurId.value, fileName)
      if (!signedUrl) {
        throw new Error('PDF nog niet beschikbaar')
      }

      if (fileName) {
        triggerFileDownload(signedUrl, fileName)
      } else {
        window.open(signedUrl, '_blank', 'noopener,noreferrer')
      }
    } catch (err) {
      console.error('Error downloading factuur PDF:', err)
      toast.error(err instanceof Error ? err.message : 'Kon factuur PDF niet downloaden')
    }
  }

  // Een betaalde, vervallen of gecrediteerde factuur hoort geen betaalknop meer
  // te tonen; de link kan technisch nog even werken (Mollie archiveert asynchroon).
  const kanBetalen = computed(
    () => Boolean(factuur.value?.payment_link) && factuur.value?.status === 'verzonden',
  )

  function openPaymentLink() {
    if (!factuur.value?.payment_link) return
    window.open(factuur.value.payment_link, '_blank', 'noopener,noreferrer')
  }

  watch(factuurId, () => {
    void loadFactuur()
  })

  onMounted(() => {
    void loadFactuur()
  })
</script>

<template>
  <DetailsLayout>
    <template #header>
      <AppHeader
        :title="factuurTitle"
        :show-back-button="true"
        back-to="/facturen"
        back-text="Alle facturen"
      />
    </template>

    <div class="factuur-details-page">
      <div
        v-if="facturenStore.loading"
        class="status-card status-loading"
      >
        <i class="pi pi-spin pi-spinner text-4xl"></i>
      </div>

      <div
        v-else-if="facturenStore.error"
        class="status-card status-error"
      >
        {{ facturenStore.error }}
      </div>

      <div
        v-else-if="!factuur"
        class="status-card status-empty"
      >
        Factuur niet gevonden.
      </div>

      <template v-else>
        <Message
          v-if="isOverdue"
          severity="warn"
          :closable="false"
        >
          Deze factuur staat open na de vervaldatum.
        </Message>

        <Message
          v-if="factuur.rapport_na_betaling && factuur.status !== 'betaald'"
          severity="info"
          :closable="false"
        >
          Na ontvangst van uw betaling ontvangt u het inspectierapport automatisch per e-mail.
        </Message>

        <div class="details-grid">
          <div class="details-main">
            <section class="info-card">
              <div class="info-card-header">
                <div>
                  <h2 class="info-card-title">Factuurinformatie</h2>
                  <p class="info-card-subtitle">Overzicht van de verstuurde factuur en gekoppelde opdrachtgever.</p>
                </div>
                <CustomTag
                  :color="getStatusColor(factuur.status)"
                  :label="getStatusLabel(factuur.status)"
                />
              </div>

              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">Factuurnummer</span>
                  <span class="info-value">{{ factuur.factuurnr || 'Wordt gevuld bij verzenden' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Opdrachtgever</span>
                  <span class="info-value">{{ opdrachtgeverLabel }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Inspectiebureau</span>
                  <span class="info-value">{{ inspectiebureauLabel }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Factuurdatum</span>
                  <span class="info-value">{{ formatDate(factuur.factuur_datum) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Vervaldatum</span>
                  <span class="info-value">{{ formatDate(factuur.vervaldatum) }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Vervaltermijn</span>
                  <span class="info-value">{{ factuur.vervaltermijn_dagen ? `${factuur.vervaltermijn_dagen} dagen` : '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Opmerking</span>
                  <span class="info-value">{{ factuur.opmerkingen || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Betaaldatum</span>
                  <span class="info-value">{{ formatDate(factuur.betaald_datum) }}</span>
                </div>
              </div>
            </section>

            <section class="info-card">
              <div class="section-header">
                <h2 class="info-card-title">Producten</h2>
                <span class="section-meta">{{ factuur.producten?.length || 0 }} producten</span>
              </div>

              <div
                v-if="!factuur.producten?.length"
                class="empty-inline"
              >
                Geen producten beschikbaar.
              </div>

              <div
                v-else
                class="line-items"
              >
                <div
                  v-for="product in (factuur.producten || []) as FactuurProduct[]"
                  :key="product.id"
                  class="line-item"
                >
                  <div class="line-item-main">
                    <div class="line-item-title">{{ product.omschrijving }}</div>
                    <div class="line-item-meta">
                      <span>{{ product.aantal }} x {{ formatCurrency(product.prijs) }}</span>
                      <span>BTW {{ product.btw_rate }}%</span>
                    </div>
                  </div>
                  <div class="line-item-total">{{ formatCurrency(product.regel_totaal_incl_btw) }}</div>
                </div>
              </div>
            </section>

            <section class="info-card">
              <div class="section-header">
                <h2 class="info-card-title">Herinneringen</h2>
                <span class="section-meta">{{ factuur.herinneringen?.length || 0 }} items</span>
              </div>

              <div
                v-if="!factuur.herinneringen?.length"
                class="empty-inline"
              >
                Nog geen ontvangen herinneringen.
              </div>

              <div
                v-else
                class="reminder-list"
              >
                <div
                  v-for="herinnering in (factuur.herinneringen || []) as FactuurHerinnering[]"
                  :key="herinnering.id"
                  class="reminder-item"
                >
                  <div class="reminder-header">
                    <span class="reminder-title">Herinnering {{ herinnering.level }}</span>
                    <span class="reminder-status">{{ herinnering.status }}</span>
                  </div>
                  <div class="reminder-meta">Gepland: {{ formatDate(herinnering.scheduled_at, true) }}</div>
                  <div
                    v-if="herinnering.sent_at"
                    class="reminder-meta"
                  >
                    Verzonden: {{ formatDate(herinnering.sent_at, true) }}
                  </div>
                  <div
                    v-if="herinnering.error"
                    class="reminder-error"
                  >
                    {{ herinnering.error }}
                  </div>
                </div>
              </div>
            </section>
          </div>

          <aside class="details-aside">
            <section class="summary-card">
              <h2 class="summary-title">Samenvatting</h2>
              <div class="summary-row">
                <span>Subtotaal</span>
                <strong>{{ formatCurrency(factuur.subtotaal) }}</strong>
              </div>
              <div class="summary-row">
                <span>BTW</span>
                <strong>{{ formatCurrency(factuur.btw_totaal) }}</strong>
              </div>
              <div class="summary-row summary-total">
                <span>Totaal</span>
                <strong>{{ formatCurrency(factuur.totaal) }}</strong>
              </div>
              <div class="summary-row">
                <span>Betaald bedrag</span>
                <strong>{{ formatCurrency(factuur.betaald_bedrag) }}</strong>
              </div>
            </section>

            <section class="summary-card action-card">
              <h2 class="summary-title">Acties</h2>
              <Button
                label="Download PDF"
                icon="pi pi-file-pdf"
                severity="secondary"
                outlined
                :loading="facturenStore.downloading"
                @click="downloadPdf"
              />
              <Button
                v-if="kanBetalen"
                label="Betaal nu"
                icon="pi pi-external-link"
                @click="openPaymentLink"
              />
            </section>
          </aside>
        </div>
      </template>
    </div>
  </DetailsLayout>
</template>

<style scoped>
  .factuur-details-page {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 0 2rem;
  }

  .status-card {
    border-radius: var(--radius-sm);
    border: 1px solid var(--p-background-200);
    background: var(--p-background-0);
    padding: 1.25rem;
    text-align: center;
    color: var(--p-background-600);
  }

  .status-error {
    border-color: var(--p-red-200);
    background: var(--p-red-50);
    color: var(--p-red-600);
  }

  .details-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 20rem;
    gap: 1rem;
    align-items: start;
  }

  .details-main,
  .details-aside {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .info-card,
  .summary-card {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    padding: 1.25rem;
  }

  .info-card-header,
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .info-card-title,
  .summary-title {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--p-background-900);
  }

  .info-card-subtitle,
  .section-meta {
    margin: 0.35rem 0 0;
    font-size: 0.9rem;
    color: var(--p-background-600);
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .info-label {
    font-size: 0.75rem;
    color: var(--p-background-500);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .info-value {
    font-size: 0.95rem;
    color: var(--p-background-800);
    word-break: break-word;
  }

  .line-items,
  .reminder-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .line-item,
  .reminder-item {
    border: 1px solid var(--p-background-200);
    background: var(--p-background-50);
    border-radius: 0.5rem;
    padding: 0.9rem 1rem;
  }

  .line-item {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: center;
  }

  .line-item-main {
    min-width: 0;
  }

  .line-item-title,
  .reminder-title {
    font-weight: 600;
    color: var(--p-background-900);
  }

  .line-item-meta,
  .reminder-meta {
    margin-top: 0.3rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    font-size: 0.85rem;
    color: var(--p-background-600);
  }

  .line-item-total {
    font-weight: 700;
    color: var(--p-background-900);
    white-space: nowrap;
  }

  .reminder-header {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    align-items: center;
  }

  .reminder-status {
    font-size: 0.75rem;
    text-transform: uppercase;
    color: var(--p-background-500);
  }

  .reminder-error {
    margin-top: 0.35rem;
    color: var(--p-red-600);
    font-size: 0.85rem;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.6rem 0;
    border-bottom: 1px solid var(--p-background-200);
    color: var(--p-background-800);
  }

  .summary-row:last-child {
    border-bottom: none;
  }

  .summary-total {
    font-size: 1rem;
  }

  .action-card {
    gap: 0.75rem;
  }

  .action-card :deep(.p-button) {
    width: 100%;
  }

  .empty-inline {
    color: var(--p-background-600);
    font-size: 0.95rem;
  }

  @media (max-width: 960px) {
    .details-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 640px) {
    .factuur-details-page {
      padding-top: 1rem;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .line-item,
    .reminder-header,
    .info-card-header,
    .section-header {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>
